import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AddMasterResponse,
  AddVehicleChecklistResponse,
  LISTITEM,
  MasterCommonResponse,
  MasterItem,
  MasterResponse,
  VehicleChecklistResponse,
  VehicleChecklistItem,
} from '@core/interface/configuration';
import { PagePayload } from '@core/interface/core';
import { environment } from '@environment/environment';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  constructor(private http: HttpClient) {}

  getBaseLocations(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<MasterResponse>(
      environment.endPoints.baseLocationList,
      {
        params: httpParams,
      }
    );
  }

  addBaseLocation(data: MasterItem) {
    return this.http.post<AddMasterResponse>(
      environment.endPoints.baseLocationAddEdit,
      data
    );
  }
  deleteBaseLocation(id: number) {
    return this.http.get<MasterCommonResponse>(
      environment.endPoints.baseLocationDelete(id)
    );
  }

  getMakes(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<MasterResponse>(environment.endPoints.makeList, {
      params: httpParams,
    });
  }

  addMake(data: MasterItem) {
    return this.http.post<AddMasterResponse>(
      environment.endPoints.makeAddEdit,
      data
    );
  }
  deleteMake(id: number) {
    return this.http.get<MasterCommonResponse>(
      environment.endPoints.makeDelete(id)
    );
  }

  getModels(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<MasterResponse>(environment.endPoints.modelList, {
      params: httpParams,
    });
  }

  addModel(data: MasterItem) {
    return this.http.post<AddMasterResponse>(
      environment.endPoints.modelAddEdit,
      data
    );
  }
  deleteModel(id: number) {
    return this.http.get<MasterCommonResponse>(
      environment.endPoints.modelDelete(id)
    );
  }

  getYearManufactures(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<MasterResponse>(
      environment.endPoints.yearManufactureList,
      {
        params: httpParams,
      }
    );
  }

  addYearManufacture(data: MasterItem) {
    return this.http.post<AddMasterResponse>(
      environment.endPoints.yearManufactureAddEdit,
      data
    );
  }
  deleteYearManufacture(id: number) {
    return this.http.get<MasterCommonResponse>(
      environment.endPoints.yearManufactureDelete(id)
    );
  }

  getFuelType(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<MasterResponse>(environment.endPoints.fuelTypeList, {
      params: httpParams,
    });
  }

  addFuelType(data: MasterItem) {
    return this.http.post<AddMasterResponse>(
      environment.endPoints.fuelTypeAddEdit,
      data
    );
  }
  deleteFuelType(id: number) {
    return this.http.get<MasterCommonResponse>(
      environment.endPoints.fuelTypeDelete(id)
    );
  }

  getEngineSize(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<MasterResponse>(environment.endPoints.engineSizeList, {
      params: httpParams,
    });
  }

  addEngineSize(data: MasterItem) {
    return this.http.post<AddMasterResponse>(
      environment.endPoints.engineSizeAddEdit,
      data
    );
  }
  deleteEngineSize(id: number) {
    return this.http.get<MasterCommonResponse>(
      environment.endPoints.engineSizeDelete(id)
    );
  }

  getCategory(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<MasterResponse>(environment.endPoints.categoryList, {
      params: httpParams,
    });
  }

  addCategory(data: MasterItem) {
    return this.http.post<AddMasterResponse>(
      environment.endPoints.categoryAddEdit,
      data
    );
  }
  deleteCategory(id: number) {
    return this.http.get<MasterCommonResponse>(
      environment.endPoints.categoryDelete(id)
    );
  }

  getPurchaseLeaseList(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<MasterResponse>(
      environment.endPoints.purchaseLeaseList,
      {
        params: httpParams,
      }
    );
  }

  addPurchaseLease(data: MasterItem) {
    return this.http.post<AddMasterResponse>(
      environment.endPoints.purchaseLeaseAddEdit,
      data
    );
  }
  deletePurchaseLease(id: number) {
    return this.http.get<MasterCommonResponse>(
      environment.endPoints.purchaseLeaseDelete(id)
    );
  }

  getMasterList(key: LISTITEM, data: PagePayload) {
    switch (key) {
      case LISTITEM.BaseLocation:
        return this.getBaseLocations(data);
      case LISTITEM.Make:
        return this.getMakes(data);
      case LISTITEM.Model:
        return this.getModels(data);
      case LISTITEM.YearofManufacture:
        return this.getYearManufactures(data);
      case LISTITEM.FuelType:
        return this.getFuelType(data);
      case LISTITEM.EngineSize:
        return this.getEngineSize(data);
      case LISTITEM.Category:
        return this.getCategory(data);
      case LISTITEM.PurchaseLease:
        return this.getPurchaseLeaseList(data);
      default:
        return this.getBaseLocations(data);
    }
  }

  editMaster(key: LISTITEM, data: MasterItem) {
    switch (key) {
      case LISTITEM.BaseLocation:
        return this.addBaseLocation(data);
      case LISTITEM.Make:
        return this.addMake(data);
      case LISTITEM.Model:
        return this.addModel(data);
      case LISTITEM.YearofManufacture:
        return this.addYearManufacture(data);
      case LISTITEM.FuelType:
        return this.addFuelType(data);
      case LISTITEM.EngineSize:
        return this.addEngineSize(data);
      case LISTITEM.Category:
        return this.addCategory(data);
      case LISTITEM.PurchaseLease:
        return this.addPurchaseLease(data);
      default:
        return this.addBaseLocation(data);
    }
  }
  deleteMaster(key: LISTITEM, id: number) {
    switch (key) {
      case LISTITEM.BaseLocation:
        return this.deleteBaseLocation(id);
      case LISTITEM.Make:
        return this.deleteMake(id);
      case LISTITEM.Model:
        return this.deleteModel(id);
      case LISTITEM.YearofManufacture:
        return this.deleteYearManufacture(id);
      case LISTITEM.FuelType:
        return this.deleteFuelType(id);
      case LISTITEM.EngineSize:
        return this.deleteEngineSize(id);
      case LISTITEM.Category:
        return this.deleteCategory(id);
      case LISTITEM.PurchaseLease:
        return this.deletePurchaseLease(id);
      default:
        return this.deleteBaseLocation(id);
    }
  }

  getVehicleChecklist(data?: PagePayload) {
    const filter = { ...data };
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<VehicleChecklistResponse>(
      environment.endPoints.dvcConfig,
      {
        params: httpParams,
      }
    );
  }
  editVehicleChecklist(data: VehicleChecklistItem) {
    return this.http.post<AddVehicleChecklistResponse>(
      environment.endPoints.dvcConfigAddEdit,
      data
    );
  }
}
